import { useState } from 'react';
import { type SupportedCountryCode } from '@zep-us/i18n';
import { cn } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import type { CategoryTreeBuildDto } from '@/api/gen/_model/categoryTreeBuildDto';
import { ChevronLeftIcon } from '@/icons';
import { type CategoryTree } from '@/shared/category/type';
import { BetaChip } from '@/shared/components/Chip/BetaChip';
import { type MapTemplate, type MapTemplateType } from '@/types/MapTemplate';
import { DialogHeader, IconButton, ResponsiveDialog } from '@/ui_v3';
import { type BuildType } from '@/views/build/context/BuildInfoProvider';
import { type SelectionSchoolInfo } from '../../../type';
import { type CategoryInfoInputType } from '../../category-info';
import { CategoryInfoView } from '../../category-select-view';
import { MapSelectionView } from '../../map-selection';
import { isQuizBankSupported, QUIZ_BANK_MODAL_SIZE_CLASS, type QuizBankProblem, QuizBankViewContainer, toQuizBankCategoryTreeId } from '../../quiz-bank';
import { type QuizCreateMethod, QuizCreateMethodView } from './QuizCreateMethodView';
type CurrentView = 'MAP' | 'EDUCATION' | 'METHOD' | 'BANK';
const PREVIOUS_VIEW: Record<Exclude<CurrentView, 'MAP'>, CurrentView> = {
  EDUCATION: 'MAP',
  METHOD: 'EDUCATION',
  BANK: 'METHOD'
};
export interface QuizSetupModalProps {
  /** 맵 템플릿 목록 */
  mapTemplates: MapTemplate[];
  /** 선택된 맵 템플릿 ID (선택사항) */
  selectedMapTemplateId?: string;
  /** build form 정보들 */
  initialCategoryInfoValues: CategoryInfoInputType;
  /** BuildInfo의 학교 정보 트리 */
  schoolInfoTree: CategoryTree;
  /** BuildInfo의 과목 트리 */
  subjectTree: CategoryTreeBuildDto[];
  /** BuildInfo의 출판사 정보 */
  publishers: SelectionSchoolInfo['publishers'];
  /** 사용자 국가 코드 */
  userCountryCode: SupportedCountryCode;
  /** 서버가 노출 중인 문제 은행의 카테고리 접두. 비어 있으면 만들기 방법·은행 단계를 건너뛴다 */
  quizBankExposedCategoryPrefixes: string[];
  /** 빌드 진입 타입 */
  buildType: BuildType;
  /** 문제 은행에서 담을 수 있는 남은 문항 수 */
  quizBankRemainingSlots: number;
  /** 이미 빌드에 담긴 은행 문항 id. 저장된 초안을 이어서 만들면 값이 있을 수 있다 */
  quizBankAddedProblemIds: string[];
  /** 맵 선택 완료 시 호출 (선택된 맵 ID와 타입 전달) */
  onMapComplete: (selectedMapTemplateId: string, mapTemplateType: MapTemplateType) => void;
  /** 교육정보 입력 완료 시 호출 */
  onCategoryInfoComplete: (values: CategoryInfoInputType) => void;
  /** 문제 은행에서 담기 완료 시 호출. 담지 않고 건너뛰면 빈 배열 */
  onQuizBankComplete: (problems: QuizBankProblem[]) => void;
  /** 모달 닫기 시 호출 */
  onClose?: () => void;
}

/**
 * 신규 퀴즈 생성 통합 모달: 맵 선택 → 교육정보 → (은행이 노출된 카테고리면) 만들기 방법 분기 → 문제 은행.
 * 분기에서 "직접 만들기"를 고르면 은행을 건너뛰고 닫힌다.
 */
export const QuizSetupModal = ({
  mapTemplates,
  selectedMapTemplateId,
  initialCategoryInfoValues,
  schoolInfoTree,
  subjectTree,
  publishers,
  userCountryCode,
  quizBankExposedCategoryPrefixes,
  buildType,
  quizBankRemainingSlots,
  quizBankAddedProblemIds,
  onMapComplete,
  onCategoryInfoComplete,
  onQuizBankComplete,
  onClose
}: QuizSetupModalProps) => {
  const {
    t
  } = useTranslation(['build']);
  const [currentView, setCurrentView] = useState<CurrentView>('MAP');
  // 뒤로 가서 `CategoryInfoView` 가 다시 마운트될 때 방금 고른 값을 유지한다.
  const [categoryValues, setCategoryValues] = useState(initialCategoryInfoValues);
  const [quizBankCategoryTreeId, setQuizBankCategoryTreeId] = useState<string | null>(null);

  // 맵 선택 완료 핸들러
  const handleMapComplete = (selectedMapTemplateId: string, mapTemplateType: MapTemplateType) => {
    // 상위에 맵 선택 완료 알림
    onMapComplete(selectedMapTemplateId, mapTemplateType);

    // 맵 선택 완료 이벤트 트래킹 (main 브랜치와 동일)
    logger.track('map_select_completed', {
      from: 'enter_build_page',
      is_change: false,
      build_type: buildType
    });

    // 카테고리 정보 입력 모달 오픈 이벤트 트래킹
    logger.track('category_input_modal_open', {
      trigger: 'new'
    });

    // CategoryInfoView로 전환
    setCurrentView('EDUCATION');
  };
  const handleCategoryInfoComplete = (values: CategoryInfoInputType) => {
    setCategoryValues(values);
    onCategoryInfoComplete(values);
    const categoryTreeId = values.subSubject || values.subject;
    if (!isQuizBankSupported(categoryTreeId, {
      exposedCategoryPrefixes: quizBankExposedCategoryPrefixes
    })) {
      // 서버가 노출한 은행 카테고리가 아니면 분기·은행 단계 없이 빈 배열로 완료해 모달을 닫는다.
      onQuizBankComplete([]);
      return;
    }
    setQuizBankCategoryTreeId(toQuizBankCategoryTreeId(categoryTreeId));
    setCurrentView('METHOD');
  };
  const handleCreateMethodSelect = (method: QuizCreateMethod) => {
    if (method === 'DIRECT') {
      onQuizBankComplete([]);
      return;
    }
    setCurrentView('BANK');
  };

  // 뒤로가기 핸들러
  const handleBack = () => {
    if (currentView === 'MAP') return;
    setCurrentView(PREVIOUS_VIEW[currentView]);
  };

  /*
    닫기는 문제 은행 단계에서만 연결한다.
     맵·교육정보는 건너뛸 수 없는 설정이다 — 확정하지 않은 채 빌드로 들어가면
    `templateId`·`subject` 가 빈 값으로 남고, `BuildQuizSetBasicInfoSchema` 가 둘 다
    `min(1)` 이라 나중에 작성한 퀴즈를 게시할 수 없다. 만들기 방법 단계는 "직접 만들기"가
    나가는 길이고, 은행 단계는 "담지 않고 나가기"가 정상 경로라 여기서만 닫기를 준다.
  */
  const canClose = currentView === 'BANK';
  const handleClose = canClose ? () => onClose?.() : undefined;

  // 현재 뷰에 따른 제목
  const title: string = currentView === 'MAP' ? t('build:mapTemplate.selectQuizMap') as string : currentView === 'EDUCATION' ? t('build:build.quizSetupModal.additionalInfoTitle') as string : currentView === 'METHOD' ? t('build:quizBank.createMethod.title') as string : t('build:quizBank.title') as string;
  return <ResponsiveDialog size="2xl"
  // 크기 확대는 BANK 단계에만 — 맵 미리보기는 모달을 키우면 이미지와 설명 사이가 벌어진다.
  className={cn('h-[90%] max-h-[800px] tablet-sm:gap-xs', currentView === 'BANK' && QUIZ_BANK_MODAL_SIZE_CLASS)}
  // 핸들러를 빼는 것만으로는 부족하다 — dim 클릭 경로 자체를 닫는다
  shouldCloseOnDimClick={canClose} onClose={handleClose} data-sentry-element="ResponsiveDialog" data-sentry-component="QuizSetupModal" data-sentry-source-file="QuizSetupModal.tsx">
      {/* 제목 바로 오른쪽에 Beta 칩을 두려고 `title` 대신 `leftSlot` 에 그린다 */}
      <DialogHeader leftSlot={<div className="flex min-w-0 items-center gap-xs">
            {currentView !== 'MAP' && <IconButton variant="transparent" intent="neutral" size="sm"
      /* 아이콘만 있는 버튼이라 이름이 없으면 스크린리더에 "버튼"으로만 읽힌다 */ aria-label={t('build:coachmark.backAriaLabel')} onClick={handleBack}>
                <ChevronLeftIcon size="md" />
              </IconButton>}
            <span className="truncate">{title}</span>
            {currentView === 'BANK' && <BetaChip className="shrink-0" />}
          </div>} ariaTitle={title} hasCloseButton={canClose} onClose={handleClose} data-sentry-element="DialogHeader" data-sentry-source-file="QuizSetupModal.tsx" />

      {currentView === 'MAP' && <MapSelectionView mapTemplates={mapTemplates} selectedMapTemplateId={selectedMapTemplateId} userCountryCode={userCountryCode} onComplete={handleMapComplete} />}

      {currentView === 'EDUCATION' && <CategoryInfoView formValues={categoryValues} schoolInfoTree={schoolInfoTree} subjectTree={subjectTree} publishers={publishers} userCountryCode={userCountryCode} onComplete={handleCategoryInfoComplete} trigger="new" />}

      {currentView === 'METHOD' && quizBankCategoryTreeId && <QuizCreateMethodView buildType={buildType} categoryTreeId={quizBankCategoryTreeId} onSelect={handleCreateMethodSelect} />}

      {currentView === 'BANK' && quizBankCategoryTreeId && <QuizBankViewContainer place="setup_modal" categoryTreeId={quizBankCategoryTreeId} maxSelectableCount={quizBankRemainingSlots} addedProblemIds={quizBankAddedProblemIds} onAdd={onQuizBankComplete} />}
    </ResponsiveDialog>;
};